import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import { business, services } from '../data/siteData';
import Logo from './Logo';

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-300 mt-20">
      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Logo size={48} variant="light" />
            <div>
              <div className="font-extrabold text-white text-lg">MW Stewart</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-400 font-semibold">
                Construction
              </div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-slate-400">
            A trusted family-run construction firm specializing in hot asphalt paving, protective
            seal coating, and hot rubberized crack sealing across Western Canada since {business.established}.
          </p>
          <div className="flex gap-3 mt-5">
            {[
              { Icon: Facebook, href: 'https://facebook.com/MWStewartConstruction', label: 'Facebook' },
              { Icon: Instagram, href: 'https://www.instagram.com/mw_stewart_construction', label: 'Instagram' },
            ].map(({ Icon, href, label }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-md bg-slate-800 hover:bg-amber-500 hover:text-slate-900 flex items-center justify-center transition-colors"
                aria-label={`MW Stewart Construction on ${label}`}
                title={label}
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            {[
              { label: 'Home', to: '/' },
              { label: 'About', to: '/about' },
              { label: 'Services', to: '/services' },
              { label: 'Service Area', to: '/service-area' },
              { label: 'Gallery', to: '/gallery' },
              { label: 'Contact', to: '/contact' },
              { label: 'Privacy Policy', to: '/privacy-policy' },
            ].map(({ label, to }) => (
              <li key={label}>
                <Link to={to} className="hover:text-amber-400 transition-colors">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">Our Services</h4>
          <ul className="space-y-2 text-sm">
            {services.slice(0, 6).map((s) => (
              <li key={s.id}>
                <Link to="/services" className="hover:text-amber-400 transition-colors">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">Contact</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-1 text-amber-400 shrink-0" />
              <span>{business.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-1 text-amber-400 shrink-0" />
              <a href={`tel:${business.phoneRaw}`} className="hover:text-amber-400">
                {business.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={16} className="mt-1 text-amber-400 shrink-0" />
              <div>
                <div>{business.hours}</div>
                <div>{business.hoursWeekend}</div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-slate-500">
          <p>
            © {new Date().getFullYear()} {business.name}. All rights reserved.
          </p>
          <p className="flex flex-wrap gap-x-4 gap-y-1 justify-center">
            <span>{business.tagline} · {business.subTagline}</span>
            <Link to="/privacy-policy" className="hover:text-amber-400 transition-colors">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
